import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { MatDialog } from '@angular/material';
import { ThemePalette } from '@angular/material/core';
import { MenuPositionY } from '@angular/material/menu';
import { TranslocoService } from '@ngneat/transloco';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { map } from 'rxjs/operators/map';
import { Assessment } from 'src/API';
import { PDFType } from '../dry-eye-pdf-export.component';
import { AccessState } from 'src/app/core/schemas/subscription-plan.schema';
import { SubscriptionPlanAccessService } from 'src/app/core/services/subscription-plan-access.service';

export type ButtonAppearance = ButtonAppearances | 'standard' | 'raised' | 'stroked';

export enum ButtonAppearances {
  Standard = 'standard',
  Raised = 'raised',
  Stroked = 'stroked'
}

export const ExportDestinations = {
  Download: 'download',
  Email: 'email',
  PatientApp: 'patientApp'
} as const;

export type ExportDestination = 'download' | 'email' | 'patientApp';

export interface PdfSendAction {
  destination: ExportDestination;
  label: string;
  iconName: string;
  toolTipMessage: string;
  isLocked: boolean;
  isDisabled: boolean;
}

export interface PdfButtonConfig {
  type: PDFType;
  lang: string;
  friendlyName: string;
  downloadToolTipMessage: string;
  sendActions: PdfSendAction[];
}

export type DropdownArrowDirection = DropdownArrowDirections | 'up' | 'down';

export enum DropdownArrowDirections {
  Up = 'up',
  Down = 'down'
}

@Component({
  selector: 'csi-pdf-export-button',
  templateUrl: './pdf-export-button.component.html',
  styleUrls: ['./pdf-export-button.component.css']
})
export class PdfExportButtonComponent implements OnDestroy {
  private _patientHasEmailSubject = new BehaviorSubject<boolean>(true);
  private _patientHasPostalCodeSubject = new BehaviorSubject<boolean>(true);
  private _patientHasDateOfBirthSubject = new BehaviorSubject<boolean>(true);
  private _isPatientAppActiveSubject = new BehaviorSubject<boolean>(false);
  private _isPatientEnrolledInPatientAppSubject = new BehaviorSubject<boolean>(false);
  private _isPatientReEnrollEligibleSubject = new BehaviorSubject<boolean>(false);

  @Input() appearance: ButtonAppearance = ButtonAppearances.Raised;
  @Input() set patientHasEmail(hasEmail: boolean) {
    this._patientHasEmailSubject.next(hasEmail);
  }
  @Input() set patientHasPostalCode(hasPostalCode: boolean) {
    this._patientHasPostalCodeSubject.next(hasPostalCode);
  }
  @Input() set patientHasDateOfBirth(hasDateOfBirth: boolean) {
    this._patientHasDateOfBirthSubject.next(hasDateOfBirth);
  }

  @Input() set isPatientAppActive(isActive: boolean) {
    this._isPatientAppActiveSubject.next(!!isActive);
  }

  @Input() set isPatientEnrolledInPatientApp(isEnrolled: boolean) {
    this._isPatientEnrolledInPatientAppSubject.next(!!isEnrolled);
  }

  @Input() set isPatientReEnrollEligible(isEligible: boolean) {
    this._isPatientReEnrollEligibleSubject.next(!!isEligible);
  }

  @Input() color: ThemePalette;
  @Input() menuPositionY: MenuPositionY = 'below';
  @Input() dropdownArrowDirection: DropdownArrowDirection = DropdownArrowDirections.Up;
  @Input() disabled = false;
  @Input() assessment: Assessment;

  @Input() assessmentMethod: string;
  @Input() isLocked: boolean;

  @Output() generatePdf = new EventEmitter<{
    pdfType: PDFType;
    lang: string;
    destination: ExportDestination;
    isLocked: boolean;
  }>();

  public readonly ButtonAppearances = ButtonAppearances;
  public readonly DropdownArrowDirections = DropdownArrowDirections;
  public readonly ExportDestinations = ExportDestinations;
  public destroy$: Subject<void> = new Subject<void>();

  private readonly missingPatientFieldsMessage =
    'Patient date of birth, postal code, and email are required to send the PDF assessment to the patient and Questionnaire';
  private readonly sendEnabledMessage = 'Send Assessment To Patient';
  private readonly patientAppNotEnrolledMessage =
    'This patient needs to be enrolled in the Patient App before a report can be sent there';
  private readonly patientAppReEnrollMessage =
    'This patient needs to be re-enrolled in the Patient App before a report can be sent there';
  private readonly patientAppEnabledMessage = 'Send Assessment To Patient App';
  private readonly downloadToolTipMessage = 'Download PDF';

  private readonly pdfExportTypes: {
    type: PDFType;
    friendlyName: string;
    canSendToPatient: boolean;
  }[] = [
    { type: 'detailed', friendlyName: 'Detailed PDF', canSendToPatient: true },
    { type: 'simple', friendlyName: 'Simple PDF', canSendToPatient: true },
    { type: 'summary', friendlyName: 'Summary PDF', canSendToPatient: true },
    { type: 'custom', friendlyName: 'Custom PDF', canSendToPatient: false }
  ];

  public pdfButtonConfigs$ = combineLatest([
    this._patientHasEmailSubject,
    this._patientHasPostalCodeSubject,
    this._patientHasDateOfBirthSubject,
    this._isPatientAppActiveSubject,
    this._isPatientEnrolledInPatientAppSubject,
    this._isPatientReEnrollEligibleSubject
  ]).pipe(
    map(
      ([
        hasEmail,
        hasPostalCode,
        hasDateOfBirth,
        isPatientAppActive,
        isPatientEnrolledInPatientApp,
        isPatientReEnrollEligible
      ]) => {
        const isEmailDisabled = !hasEmail || !hasPostalCode || !hasDateOfBirth;
        const emailToolTipMessage = isEmailDisabled
          ? this.missingPatientFieldsMessage
          : this.sendEnabledMessage;
        const patientAppToolTipMessage = isPatientEnrolledInPatientApp
          ? this.patientAppEnabledMessage
          : isPatientReEnrollEligible
          ? this.patientAppReEnrollMessage
          : this.patientAppNotEnrolledMessage;

        return this.pdfExportTypes.map(exportType => {
          const config: PdfButtonConfig = {
            type: exportType.type,
            lang: 'en',
            friendlyName: exportType.friendlyName,
            downloadToolTipMessage: this.downloadToolTipMessage,
            sendActions: []
          };

          if (!exportType.canSendToPatient) {
            return config;
          }

          const isSendLocked = this.isKeyLocked('sendReports.' + exportType.type);

          config.sendActions.push({
            destination: ExportDestinations.Email,
            label: 'Send by Email',
            iconName: 'email',
            toolTipMessage: emailToolTipMessage,
            isLocked: isSendLocked,
            isDisabled: isEmailDisabled
          });

          if (isPatientAppActive) {
            config.sendActions.push({
              destination: ExportDestinations.PatientApp,
              label: 'Send to Patient App',
              iconName: 'phone_iphone',
              toolTipMessage: patientAppToolTipMessage,
              isLocked: isSendLocked,
              isDisabled: !isPatientEnrolledInPatientApp
            });
          }

          return config;
        });
      }
    )
  );

  public readonly customCss = `position:absolute;
        top: 15px;
        right: 7px;
        font-size: 9.5px;
        padding: 2px 5px;
          border-radius: 8px;
          border: 2px solid #58eaff;
        font-weight:500;
        line-height: 1;
        pointer-events:none;`;

  constructor(
    public translocoService: TranslocoService,
    public subscriptionPlanAccessService: SubscriptionPlanAccessService
  ) {}

  public generatePDF(
    pdfType: PDFType,
    lang: string = 'en',
    destination: ExportDestination,
    isLocked = this.isLocked
  ) {
    this.generatePdf.emit({ pdfType, lang, destination, isLocked });
  }

  private isKeyLocked(key: string): boolean {
    const config = this.subscriptionPlanAccessService.flattenedSubscriptionPlanSchema[key];
    if (!config) return false;
    return config.accessState !== AccessState.ENABLED;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
